import type { ListingAudience } from './analytics/client';
import { previewDestinations } from './destinations';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MARKETPLACE, PREVIEW_UPDATES, PROJECT_180 } from '../core/preview';
import { IDEA_IDS } from '../core/catalog';
import type { IdeaListings } from './preview/types';
import { arrivedFrom, arrivedFromStudy, studyHomeUrl } from '../core/returnTo';
import {
  landingPageViewed,
  loginOpened,
  previewLearnMoreClicked,
  previewViewed,
  studyOpened,
  suggestionBoxOpened,
  toolOpened,
  frontDoorAnswered,
  type AnalyticsEvent,
} from '../core/events';
import PreviewNav from './components/PreviewNav';
import SuggestionBox from './components/SuggestionBox';
import { useIdentity, type Identity } from './analytics/useIdentity';
import { createAnalytics, nullAnalytics, NOTIFY_SWITCH, PREVIEW_UPDATES_SWITCH, type Analytics } from './analytics/client';
import PreviewNotify, { PREVIEW_UPDATES_COPY, type NotifyCopy } from './PreviewNotify';
import type { GetToken } from './previewNotifications';
import './preview.css';
import PreviewLayoutExperiment from './preview/PreviewLayoutExperiment';
import StampCardExperiment from './preview/StampCardExperiment';
import { browserStampsIO } from './stamps';
import { internalBSideEmail } from './preview/BSideListing';

const VARIANT = 'preview_catalog';

function track(analytics: Analytics | null, event: AnalyticsEvent) {
  try {
    analytics?.track(event);
  } catch (error) {
    console.warn('[analytics] preview tracking failed', error);
  }
}

/** The public version of the study app's /preview page, under the same header bar. */
export interface PreviewLandingProps {
  analytics?: Analytics;
  renderAccountAction?: (onLogin: () => void) => ReactNode;
  /** The signed-in visitor, or null. `undefined` while Clerk is still resolving. */
  identity?: Identity | null;
  getToken?: GetToken;
  renderNotifySignIn?: (onClick: () => void, label: string) => ReactNode;
}

export default function PreviewLanding({ analytics: injected, renderAccountAction, identity, getToken, renderNotifySignIn }: PreviewLandingProps = {}) {
  const [osaVisible, setOsaVisible] = useState(false);
  const [objectivesVisible, setObjectivesVisible] = useState(false);
  const [rubricAudience, setListingAudience] = useState<ListingAudience>('unknown');
  const [rubricVisible, setRubricVisible] = useState(false);
  const [sitesAudience, setSitesAudience] = useState<ListingAudience>('unknown');
  const [sitesVisible, setSitesVisible] = useState(false);
  const [writingFeedbackVisible, setWritingFeedbackVisible] = useState(false);
  const [writingFeedbackAudience, setWritingFeedbackAudience] = useState<ListingAudience>('unknown');
  const [collabVisible, setCollabVisible] = useState(false);
  const [collabAudience, setCollabAudience] = useState<ListingAudience>('unknown');
  const [plannerVisible, setPlannerVisible] = useState(false);
  const [plannerAudience, setPlannerAudience] = useState<ListingAudience>('unknown');
  const [profileVisible, setProfileVisible] = useState(false);
  const [profileAudience, setProfileAudience] = useState<ListingAudience>('unknown');
  const [aiDetectorVisible, setAiDetectorVisible] = useState(false);
  const [aiDetectorAudience, setAiDetectorAudience] = useState<ListingAudience>('unknown');
  const [marketplaceAudience, setMarketplaceAudience] = useState<ListingAudience>('unknown');
  const [osaAudience, setOsaAudience] = useState<ListingAudience>('unknown');
  const [marketplaceVisible, setMarketplaceVisible] = useState(false);
  const [jobsVisible, setJobsVisible] = useState(false);
  const [jobsAudience, setJobsAudience] = useState<ListingAudience>('unknown');
  const [jobsLaunch, setJobsLaunch] = useState(false);
  const [bSideVisible, setBSideVisible] = useState(false);
  const [bSideAudience, setBSideAudience] = useState<ListingAudience>('unknown');
  const [ideas, setIdeas] = useState<IdeaListings>({});
  const [notifySwitches, setNotifySwitches] = useState<{ notify: boolean; updates: boolean } | null>(null);
  const [suggestionBox, setSuggestionBox] = useState(false);
  const [analytics, setAnalytics] = useState<Analytics | null>(injected ?? null);

  useEffect(() => {
    if (injected) return;
    let live = true;
    createAnalytics()
      .catch(() => nullAnalytics())
      .then((value) => { if (live) setAnalytics(value); });
    return () => { live = false; };
  }, [injected]);

  /**
   * One visit, one `preview_viewed`, matching the study app's page: the visit is the
   * first render, and a card arriving later must not re-report it.
   *
   * Only Project 180 is named. Objectives is gated on a PostHog flag that resolves after
   * this fires, so it could never be counted here — the study app could, because its
   * catalogue is known synchronously. Counting it would mean delaying the visit event
   * behind a flag that may never answer.
   */
  // Identity must be configured before the visit is sent. Clerk loading is not sign-out.
  useIdentity(analytics, identity, getToken);
  // Stable across renders so the stamp card reads once per signed-in visitor.
  const stampsIO = useMemo(() => browserStampsIO(getToken), [getToken]);
  const reported = useRef<string | null>(null);

  useEffect(() => {
    if (!analytics) return;
    if (getToken && identity === undefined) return;
    const visitor = identity?.id ?? 'anonymous';
    if (reported.current === visitor) return;
    reported.current = visitor;
    track(analytics, landingPageViewed(VARIANT, arrivedFrom(window.location.search)));
    track(analytics, previewViewed({
      programIds: [PROJECT_180.id],
      featuredProgramIds: [PROJECT_180.id],
    }));
  }, [analytics, identity, getToken]);

  useEffect(() => {
    setOsaVisible(false);
    setOsaAudience('unknown');
    return analytics?.watchOsaVisibility?.((visible, audience = 'unknown') => {
      setOsaVisible(visible);
      setOsaAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setIdeas({});
    const watch = analytics?.watchIdeaVisibility;
    if (!watch) return;
    const stops = IDEA_IDS.map(idea => watch(idea, (visible, audience = 'unknown') => {
      setIdeas(current => ({ ...current, [idea]: { visible, audience } }));
    }));
    return () => stops.forEach(stop => stop());
  }, [analytics]);

  useEffect(() => {
    setObjectivesVisible(false);
    return analytics?.watchObjectivesVisibility?.(setObjectivesVisible);
  }, [analytics]);

  useEffect(() => {
    setSitesVisible(false);
    setSitesAudience('unknown');
    return analytics?.watchSitesVisibility?.((visible, audience = 'unknown') => {
      setSitesVisible(visible);
      setSitesAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setMarketplaceVisible(false);
    setMarketplaceAudience('unknown');
    return analytics?.watchMarketplaceVisibility?.((visible, audience = 'unknown') => {
      setMarketplaceVisible(visible);
      setMarketplaceAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setRubricVisible(false);
    setListingAudience('unknown');
    return analytics?.watchRubricVisibility?.((visible, audience = 'unknown') => {
      setRubricVisible(visible);
      setListingAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setCollabVisible(false);
    setCollabAudience('unknown');
    return analytics?.watchCollabVisibility?.((visible, audience = 'unknown') => {
      setCollabVisible(visible);
      setCollabAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setProfileVisible(false);
    setProfileAudience('unknown');
    return analytics?.watchProfileVisibility?.((visible, audience = 'unknown') => {
      setProfileVisible(visible);
      setProfileAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setAiDetectorVisible(false);
    setAiDetectorAudience('unknown');
    return analytics?.watchAiDetectorVisibility?.((visible, audience = 'unknown') => {
      setAiDetectorVisible(visible);
      setAiDetectorAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setPlannerVisible(false);
    setPlannerAudience('unknown');
    return analytics?.watchPlannerVisibility?.((visible, audience = 'unknown') => {
      setPlannerVisible(visible);
      setPlannerAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setWritingFeedbackVisible(false);
    setWritingFeedbackAudience('unknown');
    return analytics?.watchWritingFeedbackVisibility?.((visible, audience = 'unknown') => {
      setWritingFeedbackVisible(visible);
      setWritingFeedbackAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setJobsVisible(false);
    setJobsAudience('unknown');
    return analytics?.watchJobsVisibility?.((visible, audience = 'unknown') => {
      setJobsVisible(visible);
      setJobsAudience(audience);
    });
  }, [analytics]);

  useEffect(() => {
    setBSideVisible(false);
    setBSideAudience('unknown');
    return analytics?.watchBSideVisibility?.((visible, audience = 'unknown') => {
      setBSideVisible(visible);
      setBSideAudience(audience);
    });
  }, [analytics]);
  // Closed while Clerk resolves, when signed out, and for any address outside 240.
  const bSideListing = bSideVisible && identity && internalBSideEmail(identity.email) ? { visible: true, audience: bSideAudience } : null;

  useEffect(() => {
    setJobsLaunch(false);
    return analytics?.watchJobsLaunch?.(setJobsLaunch);
  }, [analytics]);

  // No flag here on purpose: the survey is the switch. See analytics/client.ts.
  useEffect(() => {
    setSuggestionBox(false);
    return analytics?.watchSuggestionBox?.(setSuggestionBox);
  }, [analytics]);

  useEffect(() => {
    setNotifySwitches(null);
    return analytics?.watchNotifySwitches?.(setNotifySwitches);
  }, [analytics]);
  const notifyOpen = analytics !== null && (notifySwitches?.notify ?? analytics.gate(NOTIFY_SWITCH));
  const updatesOpen = notifySwitches?.updates ?? (!analytics || analytics.gate(PREVIEW_UPDATES_SWITCH));
  const click = (programId: string, action: 'notify' | 'learn_more') => track(analytics, {
    name: 'landing_program_clicked',
    properties: { program_id: programId, action, source: 'public_preview' },
  });

  const onLogin = () => track(analytics, loginOpened(VARIANT));

  // Resolved once: the page never pushes history, so the arrival is fixed for its lifetime.
  const studyHref = useMemo(
    () => studyHomeUrl(window.location.search, window.location.hostname),
    [],
  );

  /**
   * The notify-me for a coming-soon program. One shape for every program: the study app
   * stores the preference against the program id, so the same button serves Project 180
   * and Marketplace with nothing but the id and the name changed.
   */
  const notifyActions = (program: { id: string; name: string }, copy?: NotifyCopy) => analytics === null ? (
    <button className="preview-notify" disabled type="button">{copy?.button ?? 'Notify me'}</button>
  ) : notifyOpen ? (
    identity && getToken ? (
      <PreviewNotify key={identity.id} program={program} copy={copy} getToken={getToken} onClick={() => click(program.id, 'notify')}
        onSaved={(notify) => track(analytics, {
          name: 'preview_program_notify_changed',
          properties: { program_id: program.id, opted_in: notify, source: 'public_preview' },
        })} />
    ) : renderNotifySignIn ? (
      <div className="preview-notify-signin">
        {renderNotifySignIn(() => click(program.id, 'notify'), copy?.button ?? 'Notify me')}
        <p className="preview-signin-note">Your 240 account holds this choice, so sign in to save it.</p>
      </div>
    ) : (
      <a className="preview-notify" href={previewDestinations(window.location.hostname).studyPreview} onClick={() => click(program.id, 'notify')}>
        {copy?.button ?? 'Notify me'}
      </a>
    )
  ) : (
    <p className="preview-unavailable">More soon. Notifications are not open right now.</p>
  );

  const programActions = (
    <>
      {notifyActions(PROJECT_180)}
      <a className="preview-learn-more" href={PROJECT_180.learnMoreUrl} target="_blank" rel="noreferrer"
        onClick={() => {
          click(PROJECT_180.id, 'learn_more');
          track(analytics, previewLearnMoreClicked(PROJECT_180.id, 'featured'));
        }}>
        Learn more about {PROJECT_180.name}
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 3h6v6M10 14 21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
        </svg>
      </a>
    </>
  );

  // No "learn more": there is no page to learn from yet. The button is the whole card.
  const marketplaceActions = notifyActions(MARKETPLACE);

  return (
    <>
      <PreviewNav
        studyHref={studyHref}
        onStudyClick={() => track(analytics, studyOpened('header', arrivedFromStudy(window.location.search)))}
        accountAction={renderAccountAction ? renderAccountAction(onLogin) : (
          <a className="preview-login" href={previewDestinations(window.location.hostname).studyPreview} onClick={onLogin}>Log in</a>
        )}
      />
      <PreviewLayoutExperiment
        analytics={analytics}
        updatesAction={updatesOpen && (
          <section className="preview-updates" aria-label="Labs updates">
            <p className="preview-updates-copy">
              When something new lands in Labs, one email.
              <span>Never more than one a month. Unsubscribe anytime.</span>
            </p>
            {notifyActions(PREVIEW_UPDATES, PREVIEW_UPDATES_COPY)}
          </section>
        )}
        programActions={programActions}
        marketplaceActions={marketplaceActions}
        marketplaceVisible={marketplaceVisible}
        marketplaceAudience={marketplaceAudience}
        osaVisible={osaVisible}
        osaAudience={osaAudience}
        onOsaLearnMore={() => track(analytics, previewLearnMoreClicked('one-step-ahead', 'featured'))}
        objectivesVisible={objectivesVisible}
        sitesVisible={sitesVisible}
        sitesAudience={sitesAudience}
        rubricVisible={rubricVisible}
        rubricAudience={rubricAudience}
        writingFeedbackVisible={writingFeedbackVisible}
        writingFeedbackAudience={writingFeedbackAudience}
        collabVisible={collabVisible}
        collabAudience={collabAudience}
        plannerVisible={plannerVisible}
        plannerAudience={plannerAudience}
        profileVisible={profileVisible}
        profileAudience={profileAudience}
        aiDetectorVisible={aiDetectorVisible}
        aiDetectorAudience={aiDetectorAudience}
        jobsVisible={jobsVisible}
        jobsAudience={jobsAudience}
        jobsHref={jobsLaunch ? previewDestinations(window.location.hostname).jobs : undefined}
        bSideListing={bSideListing}
        ideas={ideas}
        showVisibilityDetails={/^[^@]+@240tutoring\.com$/.test(identity?.email?.trim().toLowerCase() ?? '')}
        onToolOpen={tool => track(analytics, toolOpened(tool))}
        studyHref={studyHref}
        renderStampCard={listings => (
          <StampCardExperiment analytics={analytics} identity={identity} listings={listings} io={stampsIO}
            onToolOpen={tool => track(analytics, toolOpened(tool, 'stamp_card'))} />
        )}
        onFrontDoorAnswer={(answer, details) => {
          track(analytics, frontDoorAnswered(answer, details));
          // The existing funnels keep reading their own events for the same click.
          if (details.tool) track(analytics, toolOpened(details.tool, 'front_door'));
          if (answer === 'not_teaching') track(analytics, studyOpened('front_door', arrivedFromStudy(window.location.search)));
        }}
      />
      <SuggestionBox
        available={suggestionBox}
        onOpen={() => track(analytics, suggestionBoxOpened('public_preview'))}
      />
    </>
  );
}
